import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { getSession, onAuthStateChange } from '../lib/auth'
import { getProfile, upsertProfile } from '../lib/profiles'

const PENDING_KEY = 'ghost-pending-signup'

// Provides { user, profile, loading, refresh } to the whole tree.
// `user` = supabase auth user (has id, email, metadata). `profile` = the
// row from public.profiles for that user.

const Ctx = createContext(null)

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadProfile = useCallback(async (u) => {
    if (!u) {
      setProfile(null)
      return
    }
    try {
      let p = await getProfile(u.id)

      // Bootstrap: if the profile row doesn't exist yet (e.g. after email
      // confirmation, since Signup couldn't upsert without a session) or is
      // missing the address the user typed at signup, flush the pending
      // stash into it and clear localStorage.
      let pending = null
      try {
        const raw = localStorage.getItem(PENDING_KEY)
        pending = raw ? JSON.parse(raw) : null
      } catch { /* ignore malformed */ }

      const needsBootstrap =
        !p ||
        (pending && pending.email === (u.email || pending.email) &&
          !p.address_line1 && (pending.address_line1 || pending.city))

      if (needsBootstrap) {
        const meta = u.user_metadata || {}
        const [firstFromMeta, ...restFromMeta] = String(meta.full_name || '').split(' ').filter(Boolean)
        const seed = {
          email: u.email || pending?.email || '',
          first_name: pending?.first_name || firstFromMeta || '',
          last_name: pending?.last_name || restFromMeta.join(' ') || '',
          phone: pending?.phone || meta.phone || '',
          address_line1: pending?.address_line1 || '',
          address_line2: pending?.address_line2 || '',
          city: pending?.city || '',
          state: pending?.state || '',
          zip: pending?.zip || '',
          hotel_name: pending?.hotel_name || meta.hotel_name || '',
          room_number: pending?.room_number || meta.room_number || '',
        }
        try {
          p = await upsertProfile(u.id, seed)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[auth] profile bootstrap failed', err)
        }
        try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
      }

      setProfile(p)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[auth] profile fetch failed', err)
      setProfile(null)
    }
  }, [])

  const refresh = useCallback(async () => {
    const session = await getSession()
    setUser(session?.user || null)
    await loadProfile(session?.user)
  }, [loadProfile])

  useEffect(() => {
    let alive = true

    // Initial session hydration.
    ;(async () => {
      const session = await getSession()
      if (!alive) return
      setUser(session?.user || null)
      await loadProfile(session?.user)
      setLoading(false)
    })()

    // React to sign-in / sign-out / token refresh from Supabase.
    // The callback must stay synchronous: awaiting another Supabase call in
    // here deadlocks the auth lock during a token refresh (app resumes →
    // refresh → profile query waits on the lock the refresh holds), which
    // looks like being logged out. Defer the profile load to the next tick.
    const sub = onAuthStateChange((_event, session) => {
      if (!alive) return
      setUser(session?.user || null)
      setTimeout(() => { if (alive) loadProfile(session?.user) }, 0)
    })

    return () => {
      alive = false
      sub?.unsubscribe?.()
    }
  }, [loadProfile])

  return (
    <Ctx.Provider value={{ user, profile, loading, refresh }}>
      {children}
    </Ctx.Provider>
  )
}
